import { useMemo, useState } from "react";
import { Button } from "reactstrap";

import useAlertContext from "Hooks/useAlertContext";
import useFetch from "Hooks/useFetch";
import { TestGroup } from "Pages/Setup/Connect/connectData";
import styles from "Pages/Setup/Connect/Onboarding.module.css";

interface Props {
    systemName: string;
    subtitle: string;
    logoText: string;
    logoClass?: string;
    groups: TestGroup[];
    connectionView: string;
    goTo: (view: string) => void;
    runSystem?: string;
}

/** Endpoint test view used by onboarding. Supports static display and optional
 * live execution when runSystem is provided. */
export default function SystemTests({
    systemName,
    subtitle,
    logoText,
    logoClass,
    groups,
    connectionView,
    goTo,
    runSystem,
}: Props) {
    const { post } = useFetch();
    const { newAlert } = useAlertContext();
    const [isRunning, setIsRunning] = useState(false);
    const [localGroups, setLocalGroups] = useState<TestGroup[]>(() =>
        groups.map((g) => ({ ...g, rows: g.rows.map((r) => ({ ...r })) }))
    );

    const rows = useMemo(() => localGroups.flatMap((g) => g.rows), [localGroups]);
    const passed = rows.filter((r) => r.state === "pass").length;
    const needsAccess = rows.filter((r) => r.state === "fail").length;

    const applyResult = (
        title: string,
        testName: string,
        result: string,
        ok: boolean
    ) => {
        setLocalGroups((prev) =>
            prev.map((g) => {
                if (g.title !== title) return g;
                return {
                    ...g,
                    rows: g.rows.map((r) =>
                        r.name === testName
                            ? {
                                  ...r,
                                  result,
                                  state: ok ? "pass" : "fail",
                              }
                            : r
                    ),
                };
            })
        );
    };

    const runAllTests = async () => {
        if (!runSystem || isRunning) return;

        setIsRunning(true);
        setLocalGroups((prev) =>
            prev.map((g) => ({
                ...g,
                rows: g.rows.map((r) => ({ ...r, state: "wait", result: "Running..." })),
            }))
        );

        for (const group of localGroups) {
            for (const row of group.rows) {
                try {
                    const response = await post(
                        `/api/test_connection/${runSystem}/${row.name}`
                    );
                    const payload = await response.json();
                    const result = payload?.result ?? "No result returned.";
                    const ok = response.ok && /success/i.test(result);
                    applyResult(group.title, row.name, result, ok);
                } catch {
                    applyResult(
                        group.title,
                        row.name,
                        "Failed to run test.",
                        false
                    );
                }
            }
        }

        newAlert({
            message: `${systemName} test loads completed.`,
            color: "success",
        });
        setIsRunning(false);
    };

    return (
        <div className={styles.panel}>
            <div className={styles.phead}>
                <div className={`${styles.logo} ${logoClass ?? ""}`}>
                    {logoText}
                </div>
                <div>
                    <div className={styles.pheadName}>
                        {systemName} endpoint tests
                    </div>
                    <div className={styles.pheadSub}>{subtitle}</div>
                </div>
            </div>

            <div className={styles.tabs}>
                <button className={styles.tab} onClick={() => goTo(connectionView)}>
                    Connect
                </button>
                <button className={`${styles.tab} ${styles.tabActive}`}>
                    Test Connection
                </button>
            </div>

            <div className={styles.sec}>
                <p className={styles.hint}>
                    Press the button below to run a series of test loads from your {systemName} instance. No data will be modified.
                </p>
                {runSystem ? (
                    <div style={{ marginBottom: 12 }}>
                        <Button
                            color="primary"
                            onClick={runAllTests}
                            disabled={isRunning}
                        >
                            {isRunning ? "Running tests..." : "Run test loads"}
                        </Button>
                    </div>
                ) : null}
                {localGroups.map((g) => (
                    <div key={g.title}>
                        <div
                            className={`${styles.grp} ${
                                g.warn ? styles.grpWarn : ""
                            }`}
                        >
                            {g.title}
                        </div>
                        <div className={styles.tests}>
                            {g.rows.map((r) => (
                                <div className={styles.trow} key={r.endpath}>
                                    <span className={styles.trowName}>
                                        <span>{r.name}</span>
                                        <span className={styles.endpath}>
                                            {r.endpath}
                                        </span>
                                    </span>
                                    <span
                                        className={`${styles.res} ${
                                            r.state === "pass"
                                                ? styles.resPass
                                                : r.state === "fail"
                                                ? styles.resFail
                                                : styles.resWait
                                        }`}
                                    >
                                        {r.state === "pass"
                                            ? "✓ "
                                            : r.state === "fail"
                                            ? "⚠ "
                                            : "○ "}
                                        {r.result}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div className={styles.footbar}>
                <Button color="link" onClick={() => goTo(connectionView)}>
                    ‹ Back to connection
                </Button>
                <span className={styles.saveState}>
                    {passed} passed
                    {needsAccess ? ` · ${needsAccess} needs access` : ""}
                </span>
            </div>
        </div>
    );
}
